import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  BookOpen, Search, Scale, FileText,
  AlertTriangle, ChevronRight, X, Filter
} from 'lucide-react';
import api from '../lib/api';

const CATEGORY_META = {
  code_penal: { label: 'Code Penal', color: 'bg-red-50 text-red-700 border-red-200' },
  code_penal_militaire: { label: 'Code Penal Militaire', color: 'bg-red-50 text-red-800 border-red-300' },
  code_procedure_penale: { label: 'Code de Procedure Penale', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  code_civil: { label: 'Code Civil', color: 'bg-green-50 text-green-700 border-green-200' },
  code_famille: { label: 'Code de la Famille', color: 'bg-purple-50 text-purple-700 border-purple-200' },
  constitution: { label: 'Constitution RDC', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  code_minier: { label: 'Code Minier', color: 'bg-stone-50 text-stone-700 border-stone-200' },
  droit_commercial: { label: 'Droit Commercial', color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  protection_enfant: { label: 'Protection de l\'Enfant', color: 'bg-pink-50 text-pink-700 border-pink-200' },
  loi_speciale: { label: 'Lois Speciales', color: 'bg-cyan-50 text-cyan-700 border-cyan-200' },
  code_route: { label: 'Code de la Route', color: 'bg-gray-50 text-gray-700 border-gray-200' },
};

const SEVERITY_META = {
  crime: { label: 'Crime', color: 'bg-red-500/10 text-red-700 border-red-300', icon: AlertTriangle },
  delit: { label: 'Delit', color: 'bg-orange-500/10 text-orange-700 border-orange-300', icon: Scale },
  contravention: { label: 'Contravention', color: 'bg-yellow-500/10 text-yellow-700 border-yellow-300', icon: FileText },
};

const LegalLibraryPage = () => {
  const { language } = useAuth();
  const [activeTab, setActiveTab] = useState('infractions');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [offenses, setOffenses] = useState([]);
  const [legalEntries, setLegalEntries] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ crimes: 0, delits: 0, contraventions: 0 });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (categoryFilter && activeTab === 'infractions') params.category = categoryFilter;

      const [offResp, libResp] = await Promise.all([
        api.get('/offenses', { params }),
        api.get('/legal-library', { params: search ? { search } : {} }),
      ]);
      setOffenses(offResp.data || []);
      setLegalEntries(libResp.data || []);

      const allOff = offResp.data || [];
      setStats({
        crimes: allOff.filter(o => o.category === 'crime').length,
        delits: allOff.filter(o => o.category === 'delit').length,
        contraventions: allOff.filter(o => o.category === 'contravention').length,
      });
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  }, [search, categoryFilter, activeTab]);

  useEffect(() => {
    const timer = setTimeout(() => loadData(), 200);
    return () => clearTimeout(timer);
  }, [loadData]);

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="legal-library-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <Scale className="w-6 h-6 text-primary" />
              Bibliotheque Juridique
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Codes, lois et infractions de la RDC - Reference pour OPJ, juges et magistrats
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="border-red-200 bg-red-50/50">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg"><AlertTriangle className="w-5 h-5 text-red-600" /></div>
              <div>
                <p className="text-2xl font-bold text-red-700">{stats.crimes}</p>
                <p className="text-xs text-red-600">Crimes</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-orange-200 bg-orange-50/50">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg"><Scale className="w-5 h-5 text-orange-600" /></div>
              <div>
                <p className="text-2xl font-bold text-orange-700">{stats.delits}</p>
                <p className="text-xs text-orange-600">Delits</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-yellow-200 bg-yellow-50/50">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg"><FileText className="w-5 h-5 text-yellow-600" /></div>
              <div>
                <p className="text-2xl font-bold text-yellow-700">{stats.contraventions}</p>
                <p className="text-xs text-yellow-600">Contraventions</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search + Filter bar */}
        <div className="flex gap-2 items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher par nom, article, peine, reference..."
              className="pl-10 pr-10"
              data-testid="legal-library-search"
            />
            {search && (
              <Button variant="ghost" size="icon" className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7" onClick={() => setSearch('')}>
                <X className="w-3 h-3" />
              </Button>
            )}
          </div>
          {activeTab === 'infractions' && (
            <div className="flex gap-1">
              {['', 'crime', 'delit', 'contravention'].map(cat => (
                <Button
                  key={cat}
                  variant={categoryFilter === cat ? 'default' : 'outline'}
                  size="sm"
                  className="h-9 text-xs"
                  onClick={() => setCategoryFilter(cat)}
                >
                  {cat === '' ? <><Filter className="w-3 h-3 mr-1" />Tous</> : cat.charAt(0).toUpperCase() + cat.slice(1)}
                </Button>
              ))}
            </div>
          )}
        </div>

        {/* Main content */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="infractions" className="gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" />
              Infractions ({offenses.length})
            </TabsTrigger>
            <TabsTrigger value="textes" className="gap-1.5">
              <BookOpen className="w-3.5 h-3.5" />
              Textes de loi ({legalEntries.length})
            </TabsTrigger>
          </TabsList>

          {/* INFRACTIONS TAB */}
          <TabsContent value="infractions" className="mt-3">
            <div className="flex gap-4">
              <Card className={`${selectedItem ? 'w-1/2' : 'w-full'} transition-all`}>
                <CardHeader className="py-3 px-4">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    {loading ? 'Chargement...' : `${offenses.length} infractions`}
                    {categoryFilter && ` (${categoryFilter})`}
                  </CardTitle>
                </CardHeader>
                <ScrollArea className="h-[calc(100vh-420px)]">
                  <CardContent className="p-2 space-y-1">
                    {offenses.map((o, i) => {
                      const sev = SEVERITY_META[o.category];
                      const src = CATEGORY_META[o.source];
                      return (
                        <div
                          key={o.offense_id || i}
                          className={`p-3 rounded-lg cursor-pointer border transition-all ${
                            selectedItem?.offense_id === o.offense_id
                              ? 'bg-primary/5 border-primary/40 shadow-sm'
                              : 'hover:bg-muted/40 border-transparent'
                          }`}
                          onClick={() => setSelectedItem(o)}
                          data-testid={`offense-row-${i}`}
                        >
                          <div className="flex items-start gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-semibold leading-tight">{o.name}</p>
                                {sev && (
                                  <Badge variant="outline" className={`text-[10px] ${sev.color}`}>
                                    {sev.label}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{o.reference || o.article || ''}</p>
                              <p className="text-xs text-red-600 dark:text-red-400 mt-0.5 line-clamp-1">{o.penalty || o.details || ''}</p>
                            </div>
                            <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 mt-1" />
                          </div>
                        </div>
                      );
                    })}
                    {!loading && offenses.length === 0 && (
                      <div className="text-center py-12 text-muted-foreground">
                        <Search className="w-8 h-8 mx-auto mb-2 opacity-30" />
                        <p className="text-sm">Aucune infraction trouvee</p>
                      </div>
                    )}
                  </CardContent>
                </ScrollArea>
              </Card>

              {/* Detail panel */}
              {selectedItem && activeTab === 'infractions' && (
                <Card className="w-1/2 sticky top-4">
                  <CardHeader className="py-3 px-4 flex flex-row items-start justify-between border-b">
                    <div className="flex-1">
                      <CardTitle className="text-lg leading-tight">{selectedItem.name}</CardTitle>
                      <div className="flex gap-1.5 mt-2">
                        {SEVERITY_META[selectedItem.category] && (
                          <Badge variant="outline" className={SEVERITY_META[selectedItem.category].color}>
                            {SEVERITY_META[selectedItem.category].label}
                          </Badge>
                        )}
                        {CATEGORY_META[selectedItem.source] && (
                          <Badge variant="secondary" className="text-[10px]">
                            {CATEGORY_META[selectedItem.source].label}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => setSelectedItem(null)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </CardHeader>
                  <CardContent className="p-4 space-y-4">
                    <div>
                      <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Reference legale</p>
                      <p className="text-sm bg-muted/50 p-2 rounded">{selectedItem.reference || selectedItem.article || 'Non renseignee'}</p>
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Description / Peine applicable</p>
                      <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 p-3 rounded-lg">
                        <p className="text-sm font-medium text-red-700 dark:text-red-400">{selectedItem.penalty || selectedItem.details || 'Non renseignee'}</p>
                      </div>
                    </div>
                    {selectedItem.severity && (
                      <div>
                        <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Gravite</p>
                        <Badge variant="outline" className={`capitalize ${
                          selectedItem.severity === 'high' ? 'bg-red-100 text-red-700' :
                          selectedItem.severity === 'medium' ? 'bg-orange-100 text-orange-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {selectedItem.severity === 'high' ? 'Elevee' : selectedItem.severity === 'medium' ? 'Moyenne' : 'Faible'}
                        </Badge>
                      </div>
                    )}
                    {selectedItem.tags?.length > 0 && (
                      <div>
                        <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Mots-cles</p>
                        <div className="flex flex-wrap gap-1">
                          {selectedItem.tags.map((t, i) => (
                            <Badge key={i} variant="secondary" className="text-[10px]">{t}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* TEXTES DE LOI TAB */}
          <TabsContent value="textes" className="mt-3">
            <div className="grid gap-3">
              {legalEntries.map((e, i) => (
                <Card key={e.entry_id || i} className="hover:shadow-sm transition-shadow" data-testid={`law-entry-${i}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-primary/10 rounded-lg shrink-0">
                        <BookOpen className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0 space-y-1.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-sm font-bold">{e.title}</h3>
                          <Badge variant="outline" className={`text-[10px] ${CATEGORY_META[e.category]?.color || ''}`}>
                            {CATEGORY_META[e.category]?.label || e.category}
                          </Badge>
                          <Badge variant="secondary" className="text-[10px]">{e.articles}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{e.description}</p>
                        {e.penalties && (
                          <div className="flex items-center gap-1.5">
                            <Scale className="w-3.5 h-3.5 text-red-500" />
                            <p className="text-xs font-semibold text-red-600 dark:text-red-400">{e.penalties}</p>
                          </div>
                        )}
                        {e.tags?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {e.tags.map((t, j) => (
                              <Badge key={j} variant="secondary" className="text-[9px]">{t}</Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {!loading && legalEntries.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Aucun texte de loi trouve</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default LegalLibraryPage;
